import { useMemo, useRef, useState } from 'react'
import { uploadFile } from '../api'
import { bytes, n } from '../format'
import {
  COMPARE_MODES,
  COMPARE_OPTIONS,
  compareMode,
  compareSignature,
  describeCompare,
  toRequestCompare,
} from '../compareSpec'

/**
 * Compare the view against a CSV the user drops in, and say what is different.
 *
 * The panel is built around the one decision that has no sensible default: which
 * columns say that two rows are the same record. Everything else — which columns
 * to compare, how the two files' columns line up, what counts as equal — is
 * offered as a rule with a stated default, because a comparison that quietly
 * decided any of those would be answering a different question than the one asked.
 */

function FilePicker({ datasetId, upload, onUploaded, onClear, disabled }) {
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [over, setOver] = useState(false)

  const send = async (file) => {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      onUploaded(await uploadFile(file, datasetId), file)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
      // Let the same file be picked again after a failure, which the input would
      // otherwise treat as "no change" and ignore.
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <>
      <div
        className={`drop${over ? ' over' : ''}${busy ? ' busy' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          send(e.dataTransfer.files?.[0])
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.tsv,.txt,.psv,text/csv"
          className="drop-input"
          disabled={busy || disabled}
          onChange={(e) => send(e.target.files?.[0])}
        />
        {busy ? (
          <span className="drop-text">Opening…</span>
        ) : upload ? (
          <span className="drop-text">
            <b className="drop-name" title={upload.path}>
              {upload.name}
            </b>
            <span className="drop-meta">
              {bytes(upload.size)} · {upload.indexed ? `${n(upload.rows)} rows · ` : ''}
              {upload.headers.length} columns
            </span>
            <span className="drop-swap">Drop another file, or click to replace</span>
          </span>
        ) : (
          <span className="drop-text">
            <b>Drop a CSV here</b>
            <span className="drop-meta">or click to choose one</span>
          </span>
        )}
      </div>
      {error && <div className="alert">{error}</div>}
      {upload && !error && (
        <div className="panel-actions tight">
          <button className="btn tiny" onClick={onClear} disabled={busy}>
            Remove file
          </button>
        </div>
      )}
    </>
  )
}

/** Which columns say two rows are the same record. Nothing else can be guessed. */
function KeyColumns({ candidates, draft, setDraft }) {
  const [search, setSearch] = useState('')
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? candidates.filter((c) => c.name.toLowerCase().includes(q)) : candidates
  }, [candidates, search])

  const toggle = (name, on) => {
    setDraft((d) => {
      const keyColumns = on ? [...d.keyColumns, name] : d.keyColumns.filter((c) => c !== name)
      // A column cannot be both the thing that identifies a row and a thing being
      // compared: against itself it would only ever report "same".
      return { ...d, keyColumns, columns: d.columns.filter((c) => !keyColumns.includes(c)) }
    })
  }

  return (
    <>
      <h3 className="sub-head">Match rows on</h3>
      <p className="hint tight">
        The columns that identify a record on both sides. Several means a row has to match on all of them.
      </p>
      {candidates.length > 10 && (
        <input
          className="input tiny"
          placeholder="Find column…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="value-options dup-columns">
        {visible.map((c) => (
          <label key={c.name} className="value-option">
            <input
              type="checkbox"
              checked={draft.keyColumns.includes(c.name)}
              onChange={(e) => toggle(c.name, e.target.checked)}
            />
            <span className="value-text" title={`${c.name} → ${draft.map[c.name]}`}>
              {c.name}
            </span>
            {draft.map[c.name] !== c.name && <span className="value-count">→ {draft.map[c.name]}</span>}
          </label>
        ))}
        {visible.length === 0 && <span className="muted">No matching column.</span>}
      </div>
      {draft.keyColumns.length === 0 && (
        <p className="footnote warn-text">Pick at least one — there is no safe default for this.</p>
      )}
    </>
  )
}

/** Which columns get checked for a changed value, once two rows are paired. */
function ComparedColumns({ columns, draft, setDraft }) {
  const candidates = columns.filter((c) => draft.map[c.name] && !draft.keyColumns.includes(c.name))
  const unmapped = columns.filter((c) => !draft.map[c.name])
  const chosen = draft.columns.length

  const toggle = (name, on) =>
    setDraft((d) => ({
      ...d,
      columns: on ? [...d.columns, name] : d.columns.filter((c) => c !== name),
    }))

  return (
    <>
      <h3 className="sub-head">
        Compare these columns
        <span className="count">
          {chosen} of {candidates.length}
        </span>
      </h3>
      <div className="panel-actions tight">
        <button
          className="btn tiny"
          onClick={() => setDraft((d) => ({ ...d, columns: candidates.map((c) => c.name) }))}
        >
          All
        </button>
        <button className="btn tiny" onClick={() => setDraft((d) => ({ ...d, columns: [] }))}>
          None
        </button>
      </div>
      <div className="value-options column-list">
        {candidates.map((c) => (
          <label key={c.name} className="value-option">
            <input
              type="checkbox"
              checked={draft.columns.includes(c.name)}
              onChange={(e) => toggle(c.name, e.target.checked)}
            />
            <span className="value-text" title={`${c.name} → ${draft.map[c.name]}`}>
              {c.name}
            </span>
            {draft.map[c.name] !== c.name && <span className="value-count">→ {draft.map[c.name]}</span>}
          </label>
        ))}
        {candidates.length === 0 && <span className="muted">Nothing left to compare.</span>}
      </div>
      {unmapped.length > 0 && (
        <p className="footnote">
          {unmapped.length} column{unmapped.length === 1 ? '' : 's'} of this file
          {unmapped.length === 1 ? ' has' : ' have'} no counterpart in the upload, so
          {unmapped.length === 1 ? ' it is' : ' they are'} left out: {unmapped.slice(0, 4).map((c) => c.name).join(', ')}
          {unmapped.length > 4 ? ` +${unmapped.length - 4} more` : ''}. Pair them up below if the names differ.
        </p>
      )}
    </>
  )
}

/** How the two files' columns line up, when the names do not agree. */
function ColumnMap({ columns, fileHeaders, draft, setDraft }) {
  const paired = columns.filter((c) => draft.map[c.name]).length
  const set = (name, other) =>
    setDraft((d) => {
      const map = { ...d.map }
      if (other === '') delete map[name]
      else map[name] = other
      return {
        ...d,
        map,
        keyColumns: d.keyColumns.filter((c) => map[c]),
        columns: d.columns.filter((c) => map[c]),
      }
    })

  return (
    <details className="map-block">
      <summary>
        Column pairing
        <span className="count">
          {paired} of {columns.length} paired
        </span>
      </summary>
      <p className="hint tight">
        Matched by name, ignoring case and punctuation. Change any of them, or set one to “not compared”.
      </p>
      <div className="map-list">
        {columns.map((c) => (
          <label key={c.name} className="map-row">
            <span className="map-from" title={c.name}>
              {c.name}
            </span>
            <select
              className="input tiny"
              value={draft.map[c.name] ?? ''}
              onChange={(e) => set(c.name, e.target.value)}
            >
              <option value="">— not compared —</option>
              {fileHeaders.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </details>
  )
}

/** What counts as the same value. Every one of these is off unless it is asked for. */
function CompareOptions({ blankRules, draft, setDraft }) {
  const set = (patch) => setDraft((d) => ({ ...d, options: { ...d.options, ...patch } }))
  return (
    <>
      <h3 className="sub-head">Treat as equal</h3>
      <div className="value-options">
        {COMPARE_OPTIONS.filter((o) => !o.needs || draft.options[o.needs]).map((o) => (
          <label key={o.id} className="value-option" title={o.hint}>
            <input
              type="checkbox"
              checked={!!draft.options[o.id]}
              onChange={(e) => set({ [o.id]: e.target.checked })}
            />
            <span className="value-text">{o.label}</span>
          </label>
        ))}
      </div>
      <div className="blank-rules">
        {blankRules.map((b) => (
          <label key={b.id} className={`mode-option ${draft.options.blanks === b.id ? 'on' : ''}`} title={b.hint}>
            <input
              type="radio"
              name="compare-blanks"
              checked={draft.options.blanks === b.id}
              onChange={() => set({ blanks: b.id })}
            />
            <span className="mode-text">
              <span className="mode-label">{b.label}</span>
              {draft.options.blanks === b.id && <span className="mode-blurb">{b.hint}</span>}
            </span>
          </label>
        ))}
      </div>
    </>
  )
}

export function ComparePanel({
  datasetId,
  columns,
  blankRules,
  upload,
  onUploaded,
  onClearUpload,
  draft,
  setDraft,
  mode,
  setMode,
  applied,
  onCompare,
  onExit,
  scanning,
}) {
  const spec = useMemo(() => toRequestCompare(draft), [draft])
  const active = applied !== null
  const unchanged =
    active &&
    applied.other === upload?.id &&
    compareSignature(applied.spec) === compareSignature(spec)
  const summary = useMemo(() => describeCompare(draft, upload?.name), [draft, upload])
  const picked = compareMode(mode)
  const candidates = columns.filter((c) => draft.map[c.name])

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Compare</h2>
        {active && <span className="badge on">on</span>}
      </header>
      <p className="hint">
        Check the rows you are looking at against a CSV from somewhere else. The view is the base: every difference is
        phrased as what the file does to it.
      </p>

      <FilePicker
        datasetId={datasetId}
        upload={upload}
        onUploaded={onUploaded}
        onClear={onClearUpload}
        disabled={scanning}
      />

      {upload && (
        <>
          <div className="resolve-summary">
            <p className="summary-lead">{summary.lead}</p>
            {summary.lines.map((line) => (
              <p className="summary-line" key={line}>
                {line}
              </p>
            ))}
          </div>

          <KeyColumns candidates={candidates} draft={draft} setDraft={setDraft} />
          <ComparedColumns columns={columns} draft={draft} setDraft={setDraft} />
          <ColumnMap columns={columns} fileHeaders={upload.headers} draft={draft} setDraft={setDraft} />
          <CompareOptions blankRules={blankRules} draft={draft} setDraft={setDraft} />

          <h3 className="sub-head">Show me</h3>
          {/* Six options each with a paragraph is a wall, so only the chosen one
              explains itself — the rest just have to be identifiable. Switching
              between them is free: they are all slices of the same scan. */}
          <div className="mode-list">
            {COMPARE_MODES.map((m) => (
              <label key={m.id} className={`mode-option ${mode === m.id ? 'on' : ''}`} title={m.blurb}>
                <input
                  type="radio"
                  name="compare-mode"
                  checked={mode === m.id}
                  onChange={() => setMode(m.id)}
                />
                <span className="mode-text">
                  <span className="mode-label">{m.label}</span>
                  {mode === m.id && <span className="mode-blurb">{m.blurb}</span>}
                </span>
              </label>
            ))}
          </div>

          <div className="panel-actions">
            <button
              className="btn primary"
              onClick={() => onCompare({ other: upload.id, spec, mode })}
              disabled={!spec || unchanged}
            >
              {scanning ? 'Comparing…' : active ? 'Update' : (picked.action ?? 'Compare')}
            </button>
            {active && (
              <button className="btn subtle" onClick={onExit}>
                Stop comparing
              </button>
            )}
          </div>
        </>
      )}
    </section>
  )
}
